import React, { useEffect, useState } from 'react';
import { ISearchFilter, ISearchFilterDictionaries } from 'models/searchFilter';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import {
  StyledFilterChipWrapper,
  StyledPresetWrapper,
  StyledSelectedFiltersWrapper,
} from 'pages/MyFiltersPage/styles';
import { FilterChip } from 'components/atoms/FilterChip';
import { useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import {
  mapValuesToIndividualPresetForView,
  mapValuesToOrganisationPresetForView,
} from 'pages/LegalEntitiesPage/SavePresetPage/helpers';
import { parseDictionary, parseMultipleDictionaries } from 'helpers/dictionary';
import { colors } from 'theme/colors';
import { ESearchFilterTypes } from 'constants/SearchFilterTypes';
import { mapValuesToTransactionPresetForView } from 'pages/TransactionsPage/helpers';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { prepareFilters, preparePresetTransactionFilters } from 'helpers/filters';
import { useLang } from 'models/langContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { mapValuesToAncillaryPresetForView } from 'pages/AncillariesPage/SaveAncillariesPresetPage/helpers';
import { EArtAncillaryExpandType, EArtAncillaryType } from 'constants/ArtAccessoryType';
import { useLicenceContext } from 'models/licenceContext';
import {
  getStatusDataOptions,
  mapValuesToLicencesPresetForView,
} from 'pages/LicencesPage/helpers';
import { sortFilterChipsByLabelOrder } from 'utils/filters';
import { mapValuesWithDictionariesHelper } from 'helpers/mapValuesWithDictionariesHelper';
import { ESearchFormLabels as ESearchAccessoryFormLabels } from 'pages/AncillariesPage/AccessoriesPage/AccessoriesSearchForm';
import { ESearchFormLabels as ESearchAmmunitionFormLabels } from 'pages/AncillariesPage/AmmunitionPage/AmmunitionSearchForm';
import { ESearchFormLabels as ESearchComponentFormLabels } from 'pages/AncillariesPage/ComponentsPage/ComponentsSearchForm';

interface IPresetItem {
  preset: ISearchFilter;
  noRemoveButton?: boolean;
  labels: { [key: string]: any };
  dictionaries?: ISearchFilterDictionaries;
}

type GroupedItem = {
  key: string;
  value: any[];
};

type FilterItem = {
  key: string;
  value: string;
};

const PresetItem = ({ preset, labels, dictionaries, noRemoveButton = false }: IPresetItem) => {
  const [filtersArray, setFiltersArray] = useState<{ key: string; value: string }[]>([]);
  const { shortDateFormat, midDateFormat, getProperties } = useGlobalProperty();
  const [ancillaryDictionaryUniqueId] = useState<string | undefined>(
    preset.values.filter((val) => val.key === 'ancillaryDictionaryUniqueId')[0]?.value,
  );
  const { isOnlineLicence } = useLicenceContext();

  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const navigate = useNavigate();

  const forbiddenKeys = ['ownerId', 'keeperId'];

  const getAncillaryLabels = () => {
    switch (ancillaryDictionaryUniqueId as EArtAncillaryType) {
      case EArtAncillaryType.ACCESSORIES:
        return { ...labels, ...ESearchAccessoryFormLabels };
      case EArtAncillaryType.AMMUNITION:
        return { ...labels, ...ESearchAmmunitionFormLabels };
      case EArtAncillaryType.COMPONENTS:
        return { ...labels, ...ESearchComponentFormLabels };
      default:
        return labels;
    }
  };

  const effectiveLabels =
    preset.type === ESearchFilterTypes.ancillaries ? getAncillaryLabels() : labels;

  useEffect(() => {
    getProperties();
  }, []);
  const filtersWithDictionaries = () => {
    const clonedValues = JSON.parse(JSON.stringify(preset.values));
    let mappedValues = mapValuesWithDictionariesHelper(clonedValues, {
      organizationTypeUniqueId: parseMultipleDictionaries(
        [
          dictionaries?.organisationStateType,
          dictionaries?.organisationPoliceType,
          dictionaries?.organisationMilitaryType,
          dictionaries?.organisationCivilianType,
        ],
        'name',
      ),
      organisationCategoriesUniqueIds: parseDictionary(
        dictionaries?.organisationCategory,
        'name',
      ),
      statusUniqueId: parseDictionary(dictionaries?.legalStatus, 'name'),
      gendersUniqueIds: parseDictionary(dictionaries?.gender, 'name'),
      visibilityUniqueId: parseDictionary(dictionaries?.legalVisibility, 'name'),
      countries: parseDictionary(dictionaries?.country, 'name'),
      legalityUniqueId: parseDictionary(dictionaries?.legality, 'name'),
      statesUniqueIds: parseDictionary(dictionaries?.state, 'name'),
      types: parseDictionary(dictionaries?.firearmType, 'name'),
      actions: parseDictionary(dictionaries?.firearmAction, 'name'),
      transactionTypesUniqueIds: parseDictionary(dictionaries?.transactionType, 'name'),
      ancillaryDictionaryUniqueId: parseDictionary(dictionaries?.artAncillaryOptions, 'name'),
      ancillaryTypeUniqueId: parseMultipleDictionaries(
        [
          dictionaries?.artAccessoryType,
          dictionaries?.artAmmunitionType,
          dictionaries?.artComponentType,
        ],
        'name',
      ),
      manufacturersUniqueIds: parseMultipleDictionaries(
        [
          dictionaries?.artAccessoryManufacturerMagazine,
          dictionaries?.artAccessoryManufacturerOpticalSight,
          dictionaries?.artAccessoryManufacturerSuppressor,
          dictionaries?.artAccessoryManufacturerFlashEliminator,
          dictionaries?.artAccessoryManufacturerLaserLightModule,
          dictionaries?.artAmmunitionManufacturer,
          dictionaries?.artComponentManufacturer,
        ],
        'name',
      ),
      calibresUniqueIds: parseMultipleDictionaries(
        [
          dictionaries?.artAccessoryCalibre,
          dictionaries?.artAmmunitionCalibreGauge,
          dictionaries?.artComponentCalibre,
        ],
        'name',
      ),
      hostTypesUniqueIds: parseDictionary(dictionaries?.artAccessoryHostType, 'name'),
      magnificationsUniqueIds: parseDictionary(
        dictionaries?.artAccessoryMagnification,
        'name',
      ),
      utilitiesUniqueIds: parseDictionary(dictionaries?.artAccessoryUtility, 'name'),
      reticlesUniqueIds: parseDictionary(dictionaries?.artAccessoryReticle, 'name'),
      lightColoursUniqueIds: parseDictionary(dictionaries?.artAccessoryLightColour, 'name'),
      compositionsUniqueIds: parseMultipleDictionaries(
        [dictionaries?.artAccessoryComposition, dictionaries?.artComposition],
        'name',
      ),
      proofHousesUniqueIds: parseMultipleDictionaries(
        [dictionaries?.artAccessoryProofHouse, dictionaries?.artComponentProofHouse],
        'name',
      ),
      accessoryLegality: parseDictionary(dictionaries?.legality, 'name'),
      factoriesUniqueIds: parseDictionary(dictionaries?.artAmmunitionFactory, 'name'),
      functionalTypesUniqueIds: parseDictionary(
        dictionaries?.artAmmunitionFunctionalType,
        'name',
      ),
      cartridgeCompositionsUniqueIds: parseDictionary(
        dictionaries?.artAmmunitionCartridgeComposition,
        'name',
      ),
      coloursOfPrimerSealantUniqueIds: parseDictionary(
        dictionaries?.artAmmunitionColourOfPrimerSealant,
        'name',
      ),
      projectileShapesUniqueIds: parseDictionary(
        dictionaries?.artAmmunitionProjectileShape,
        'name',
      ),
      shotSizesUniqueIds: parseDictionary(dictionaries?.artAmmunitionShotSize, 'name'),
      munitionTypesUniqueIds: parseDictionary(dictionaries?.artAmmunitionMunitionType, 'name'),
      munitionVelocitiesUniqueIds: parseDictionary(
        dictionaries?.artAmmunitionMunitionVelocity,
        'name',
      ),
      componentCapacitiesUniqueIds: parseDictionary(
        dictionaries?.artComponentCapacity,
        'name',
      ),
      prefixGroupsUniqueIds: parseDictionary(dictionaries?.legalEntityPrefix, 'name'),
      statusesUniqueIds: prepareFilters(getStatusDataOptions(t), 'value', 'label'),
      legalTypeUniqueId: prepareFilters(dictionaries?.legalType, 'uniqueId', 'name'),
      licenceAuthorityUniqueIds: prepareFilters(
        dictionaries?.weaponLicencingAuthority,
        'uniqueId',
        'name',
      ),
      frtMakesUniqueIds: prepareFilters(dictionaries?.make, 'uniqueId', 'name'),
      frtManufacturersUniqueIds: prepareFilters(
        dictionaries?.manufacturer,
        'uniqueId',
        'name',
      ),
      frtModelsUniqueIds: prepareFilters(dictionaries?.model, 'uniqueId', 'name'),
      frtTypesUniqueIds: prepareFilters(dictionaries?.firearmType, 'uniqueId', 'name'),
    });

    switch (preset.type) {
      case ESearchFilterTypes.individual:
        mappedValues = mapValuesToIndividualPresetForView(mappedValues, shortDateFormat);
        break;
      case ESearchFilterTypes.organisation:
        mappedValues = mapValuesToOrganisationPresetForView(mappedValues);
        break;
      case ESearchFilterTypes.transaction:
        mappedValues = mapValuesToTransactionPresetForView(mappedValues, midDateFormat);
        break;
      case ESearchFilterTypes.ancillaries:
        mappedValues = mapValuesToAncillaryPresetForView(mappedValues, shortDateFormat);
        break;
      case ESearchFilterTypes.weapon_licences:
        mappedValues = mapValuesToLicencesPresetForView(mappedValues, shortDateFormat);
        break;
      default:
        break;
    }

    const preparedFiltersArray = preparePresetTransactionFilters(mappedValues, t);
    setFiltersArray(preparedFiltersArray);
  };

  useEffect(() => {
    filtersWithDictionaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage, dictionaries]);

  const handleShowResult = (presetChoosen: ISearchFilter) => {
    switch (presetChoosen.type) {
      case ESearchFilterTypes.organisation:
        navigate(`${ERouteLinks.LegalEntities}?expand=organisation`, {
          state: { presetOrganisation: presetChoosen.uniqueId },
        });
        break;
      case ESearchFilterTypes.individual:
        navigate(`${ERouteLinks.LegalEntities}?expand=individual`, {
          state: { presetIndividual: presetChoosen.uniqueId },
        });
        break;
      case ESearchFilterTypes.firearm:
        navigate(`${ERouteLinks.Firearms}?expand=registered-firearms`, {
          state: { presetFirearm: presetChoosen.uniqueId },
        });
        break;
      case ESearchFilterTypes.transaction:
        navigate(ERouteLinks.Transactions, {
          state: { presetTransaction: presetChoosen.uniqueId },
        });
        break;
      case ESearchFilterTypes.weapon_licences:
        navigate(ERouteLinks.LicencesPage, {
          state: { presetLicence: presetChoosen.uniqueId },
        });
        break;
      case ESearchFilterTypes.ancillaries: {
        const ancillaryItem = presetChoosen.values.find(
          (it) => it.key === 'ancillaryDictionaryUniqueId',
        );
        if (ancillaryItem) {
          let expandQueryString = '';
          switch (ancillaryDictionaryUniqueId as EArtAncillaryType) {
            case EArtAncillaryType.ACCESSORIES:
              expandQueryString = `?expand=${EArtAncillaryExpandType.ACCESSORIES}`;
              break;
            case EArtAncillaryType.AMMUNITION:
              expandQueryString = `?expand=${EArtAncillaryExpandType.AMMUNITION}`;
              break;
            case EArtAncillaryType.COMPONENTS:
              expandQueryString = `?expand=${EArtAncillaryExpandType.COMPONENTS}`;
              break;
            default:
              expandQueryString = '';
              break;
          }
          navigate(`${ERouteLinks.Ancillaries}${expandQueryString}`, {
            state: {
              presetAncillaries: presetChoosen.uniqueId,
              ancillaryFilterType: ancillaryItem.value,
            },
          });
        }
        break;
      }
      default:
        break;
    }
  };

  const groupByKey = (filtersArray: { key: string; value: string }[]): GroupedItem[] => {
    const grouped = Object.values(
      filtersArray
        .filter((item) => !forbiddenKeys.includes(item.key))
        .sort((a, b) => {
          if (a.key === 'ancillaryDictionaryUniqueId') return -1;
          if (b.key === 'ancillaryDictionaryUniqueId') return 1;
          const labelA = t(effectiveLabels?.[a.key as keyof typeof effectiveLabels]) || a.key;
          const labelB = t(effectiveLabels?.[b.key as keyof typeof effectiveLabels]) || b.key;
          return labelA.localeCompare(labelB);
        })
        .reduce<Record<string, GroupedItem>>((acc, { key, value }) => {
          if (!acc[key]) {
            acc[key] = { key, value: [] };
          }
          acc[key].value.push(value);
          return acc;
        }, {}),
    );
    return grouped;
  };

  const renderItems = (data: { key: string; value: string[] }[] | unknown) => {
    if (!data || !Array.isArray(data)) return '';
    return data.map((item: { key: string; value: string[] }) => (
      <FilterChip
        key={item.key}
        name={item.key}
        label={t(effectiveLabels?.[item.key as keyof typeof effectiveLabels]) || item.key}
        values={item.value.join(', ')}
      />
    ));
  };

  const groupAndSortFilterChips = (filtersArray: FilterItem[]): GroupedItem[] => {
    const labelsKeys = Object.keys(effectiveLabels);
    const groupedMap = filtersArray
      .filter((item) => !forbiddenKeys.includes(item.key))
      .reduce<Record<string, GroupedItem>>((acc, item) => {
        if (!acc[item.key]) {
          acc[item.key] = {
            key: item.key,
            value: [],
          };
        }
        acc[item.key].value.push(item.value);
        return acc;
      }, {});

    const groupedArray = Object.values(groupedMap);

    groupedArray.sort((a, b) => {
      const indexA = labelsKeys.indexOf(a.key);
      const indexB = labelsKeys.indexOf(b.key);
      return indexA - indexB;
    });

    return groupedArray;
  };

  return (
    <StyledPresetWrapper container>
      <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'space-between' }}>
        <Grid size={{ xs: 3 }}>
          <Typography variant="subtitle2">{t('saveMyFilter.filterName.label')}</Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {preset.name}
          </Typography>
        </Grid>
        <Grid size={{ xs: 3 }}>
          <Typography variant="subtitle2">{t('saveMyFilter.notification.label')}</Typography>
          <Chips
            text={
              preset.notificationEnabled
                ? t('saveMyFilter.yes.label')
                : t('saveMyFilter.no.label')
            }
            variant={
              preset.notificationEnabled ? EChipsVariants.primary : EChipsVariants.inactive
            }
          />
        </Grid>
        {isOnlineLicence() && (
          <Grid size={{ xs: 3 }}>
            <Typography variant="subtitle2">
              {t('saveMyFilter.emailNotification.label')}
            </Typography>
            <Chips
              text={
                preset.emailNotificationEnabled
                  ? t('saveMyFilter.yes.label')
                  : t('saveMyFilter.no.label')
              }
              variant={
                preset.emailNotificationEnabled
                  ? EChipsVariants.primary
                  : EChipsVariants.inactive
              }
            />
          </Grid>
        )}
        <Grid
          container
          size={{ xs: 3 }}
          spacing={2}
          sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'end' }}
        >
          <Grid sx={{ mt: 0.5 }}>
            {!noRemoveButton && (
              <Button
                label={t('saveMyFilter.showResult.button')}
                variant={EButtonVariants.outlined}
                onClick={() => {
                  handleShowResult(preset);
                }}
                size="small"
                fullWidth
              />
            )}
          </Grid>
          <Grid sx={{ mt: 0.5, mr: 2 }}>
            {!noRemoveButton && (
              <Button
                sizePX={EButtonSizesPX.px32}
                variant={EButtonVariants.outlinedIconTertiary}
                icon={EIconTypes.delete}
                onClick={() =>
                  navigate(ERouteLinks.DeleteFilter.replace(':id', preset.uniqueId))
                }
              />
            )}
          </Grid>
        </Grid>
      </Grid>

      <StyledSelectedFiltersWrapper container size={{ xs: 12 }} sx={{ rowGap: 1 }}>
        <Grid>
          <Typography variant="body2">
            {t('saveMyFilter.selectedCriteria.section')}:
          </Typography>
        </Grid>
        <StyledFilterChipWrapper>
          {filtersArray &&
            (preset.type === ESearchFilterTypes.weapon_licences
              ? renderItems(groupAndSortFilterChips(filtersArray))
              : renderItems(groupByKey(filtersArray)))}
        </StyledFilterChipWrapper>
      </StyledSelectedFiltersWrapper>

      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          display: { xs: 'flex', md: 'none' },
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          mt: {
            xs: 2,
            md: 0,
          },
        }}
        spacing={2}
      >
        <Grid size={{ xs: 12, sm: 6 }}>
          <Button
            label={t('saveMyFilter.showResult.button')}
            variant={EButtonVariants.outlined}
            onClick={() => {
              handleShowResult(preset);
            }}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {!noRemoveButton && (
            <Button
              variant={EButtonVariants.outlined}
              label={t('saveMyFilter.delete.button')}
              onClick={() =>
                navigate(ERouteLinks.DeleteFilter.replace(':id', preset.uniqueId))
              }
              size="small"
              fullWidth
            />
          )}
        </Grid>
      </Grid>
    </StyledPresetWrapper>
  );
};

export { PresetItem };
